import { z } from 'zod';

/**
 * US-502 AC-1: powody niedostępności zapisu w kolejności priorytetu (pierwszy niespełniony warunek).
 * `edition_closed` – edycja zakończona albo nieopublikowana (warunek 1). `other_edition` – osoba ma zapisy (albo odprawę)
 * w innej edycji tego samego dnia w innym mieście (BR-17; bez kroku „zgłoszenie” o edycji rodziny decydują zapisy – decyzja
 * 2026‑09‑28; edycje tego samego dnia w tym samym mieście można łączyć – decyzja 2026‑10‑03; regułę można wyłączyć w ustawieniach –
 * PRD §10 pkt 63). `travel_buffer` – zajęcia w innej edycji tego dnia kończą się albo zaczynają za blisko, żeby zdążyć przejść
 * (bufor z ustawień, domyślnie `SAME_DAY_TRAVEL_BUFFER_MINUTES`).
 */
export const unavailableReasons = [
  'cancelled',
  'no_enrollment',
  'ended',
  'edition_closed',
  'not_open_yet',
  'other_edition',
  'age',
  'children_only',
  'adults_only',
  'collision',
  'travel_buffer',
  'full',
] as const;
export const unavailableReasonSchema = z.enum(unavailableReasons);
export type UnavailableReason = z.infer<typeof unavailableReasonSchema>;

/** BR-17 (decyzja 2026‑10‑03): domyślna najmniejsza przerwa między zajęciami osoby w dwóch edycjach dnia (zmiana – ustawienia). */
export const SAME_DAY_TRAVEL_BUFFER_MINUTES = 15;

/** Dane do komunikatu (US-502 AC-3) – interfejs tylko je wyświetla, nie liczy reguł. */
export const reasonDataSchema = z.object({
  opensAt: z.string().optional(),
  participant: z.string().optional(),
  age: z.number().int().optional(),
  ageMin: z.number().int().nullable().optional(),
  ageMax: z.number().int().nullable().optional(),
  /** `collision`, `travel_buffer`: zajęcia, z którymi się nie da połączyć; `place` – gdy są w innej edycji (miejsce). */
  conflict: z
    .object({ symbol: z.string(), title: z.string(), startsAt: z.string(), endsAt: z.string(), place: z.string().optional() })
    .optional(),
  /** `travel_buffer`: wymagana przerwa w minutach (ustawienia wydarzenia). */
  bufferMinutes: z.number().int().optional(),
  /** `other_edition`: edycja tego samego dnia w innym mieście, w której osoba ma zapisy. */
  otherEdition: z.object({ name: z.string(), city: z.string() }).optional(),
});
export type ReasonData = z.infer<typeof reasonDataSchema>;

/** Stan zajęć dla wybranego uczestnika (ADR 05 §2.2 „eligibility”). */
export const eligibilityItemSchema = z.object({
  activityId: z.string().uuid(),
  state: z.enum(['enrolled', 'available', 'unavailable']),
  reason: unavailableReasonSchema.nullable(),
  data: reasonDataSchema.nullable(),
  /** US-502 AC-4, BR-36: wypis możliwy do rozpoczęcia zajęć. */
  canUnenroll: z.boolean(),
  /** US-505 AC-4: zapis zrobiony przez obsługę lub prowadzącego. */
  byStaff: z.boolean(),
});
export type EligibilityItem = z.infer<typeof eligibilityItemSchema>;

export const eligibilitySchema = z.object({
  participantId: z.string().uuid(),
  items: z.array(eligibilityItemSchema),
});
export type Eligibility = z.infer<typeof eligibilitySchema>;

/** Stan niezależny od uczestnika – widzi go też gość (US-502 AC-2). */
export const publicStates = ['open', 'cancelled', 'no_enrollment', 'ended', 'not_open_yet', 'full'] as const;

/** ADR 05 §2.2: liczba miejsc odpytywana co kilka sekund (US-507). */
export const availabilityItemSchema = z.object({
  activityId: z.string().uuid(),
  enrolled: z.number().int(),
  capacity: z.number().int(),
  reserve: z.number().int(),
  state: z.enum(publicStates),
});
export type AvailabilityItem = z.infer<typeof availabilityItemSchema>;

export const availabilitySchema = z.object({
  version: z.string(),
  opensAt: z.string(),
  items: z.array(availabilityItemSchema),
});
export type Availability = z.infer<typeof availabilitySchema>;

export const enrollRequestSchema = z.object({ activityId: z.string().uuid() });
export type EnrollRequest = z.infer<typeof enrollRequestSchema>;

/** US-507 AC-2: odpowiedź na zapis i wypis – od razu nowy stan uprawnień i liczby miejsc. */
export const enrollmentResultSchema = z.object({
  eligibility: eligibilitySchema,
  availability: availabilityItemSchema,
});
export type EnrollmentResult = z.infer<typeof enrollmentResultSchema>;
